import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  PointElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Filler,
  Legend,
  Tooltip
} from "chart.js"

Chart.register(
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  PointElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Filler,
  Legend,
  Tooltip
)

export default class extends Controller {
  static values = {
    type: { type: String, default: "line" },
    data: Object,
    options: Object
  }

  connect() {
    this.boundThemeChange = () => this.redraw()
    document.addEventListener("nw:theme-change", this.boundThemeChange)
    this.draw()
  }

  disconnect() {
    document.removeEventListener("nw:theme-change", this.boundThemeChange)
    this.chart?.destroy()
  }

  redraw() {
    this.chart?.destroy()
    this.draw()
  }

  draw() {
    const data = this.coloredData(this.dataValue || { labels: [], datasets: [] })
    this.applyChartDefaults()
    this.chart = new Chart(this.element, {
      type: this.typeValue,
      data,
      options: this.mergedOptions()
    })
  }

  applyChartDefaults() {
    const muted = this.cssVar("--nw-text-muted", "#94a3b8")
    Chart.defaults.color = muted
    Chart.defaults.borderColor = "rgba(148, 163, 184, 0.12)"
    Chart.defaults.font.family = "Inter, system-ui, sans-serif"
  }

  mergedOptions() {
    const radial = this.typeValue === "doughnut" || this.typeValue === "pie"
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          display: this.typeValue !== "bar",
          position: "bottom",
          labels: { usePointStyle: true, boxWidth: 8, padding: 16 }
        },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
          titleColor: "#fff",
          bodyColor: "#e2e8f0"
        }
      }
    }

    if (!radial) {
      base.scales = {
        x: { grid: { display: false }, ticks: { maxRotation: 0 } },
        y: {
          beginAtZero: true,
          ticks: { precision: 0 },
          grid: { color: "rgba(148, 163, 184, 0.12)" }
        }
      }
    } else {
      base.cutout = this.typeValue === "doughnut" ? "68%" : undefined
    }

    return this.deepMerge(base, this.hasOptionsValue ? this.optionsValue : {})
  }

  coloredData(raw) {
    const colors = this.themeColors()
    const datasets = (raw.datasets || []).map((dataset, index) => {
      const color = colors[index % colors.length]
      const next = { ...dataset }

      if (this.typeValue === "line") {
        next.borderColor ||= color
        next.backgroundColor ||= `${color}33`
        next.fill = next.fill !== false
        next.tension ||= 0.4
        next.pointRadius ||= 0
        next.pointHoverRadius ||= 4
        next.borderWidth ||= 2.5
      } else if (this.typeValue === "doughnut" || this.typeValue === "pie") {
        next.backgroundColor ||= colors
        next.borderWidth ||= 0
        next.hoverOffset ||= 6
      } else {
        next.backgroundColor ||= color
        next.borderRadius ||= 8
        next.borderSkipped ||= false
        next.maxBarThickness ||= 28
      }

      return next
    })

    return { ...raw, datasets }
  }

  themeColors() {
    return [
      this.cssVar("--nw-chart-1", "#6366f1"),
      this.cssVar("--nw-chart-2", "#06b6d4"),
      this.cssVar("--nw-chart-3", "#10b981"),
      this.cssVar("--nw-chart-4", "#f59e0b"),
      this.cssVar("--nw-chart-5", "#f87171")
    ]
  }

  cssVar(name, fallback) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return value || fallback
  }

  deepMerge(base, extra) {
    if (!extra || typeof extra !== "object") return base

    const result = { ...base }
    Object.entries(extra).forEach(([ key, value ]) => {
      if (value && typeof value === "object" && !Array.isArray(value) && typeof result[key] === "object") {
        result[key] = this.deepMerge(result[key], value)
      } else {
        result[key] = value
      }
    })
    return result
  }
}
